import {
  addDays,
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  endOfYear,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";
import { fr } from "date-fns/locale";
import type { IDateRangePickerChip } from "./types";

const today = new Date();

export enum DateRangePickerCommonChips {
  TODAY = "today",
  CURRENT_WEEK = "currentWeek",
  CURRENT_MONTH = "currentMonth",
  CURRENT_YEAR = "currentYear",
  LAST_WEEK = "lastWeek",
  LAST_TWO_WEEKS = "lastTwoWeeks",
  LAST_MONTH = "lastMonth",
  LAST_THREE_MONTHS = "lastThreeMonths",
  EXPIRED_TO_DATE = "expiredToDate",
  NEXT_MONTH = "nextMonth",
  NEXT_WEEK = "nextWeek",
  NEXT_TWO_WEEKS = "nextTwoWeeks",
  NEXT_THIRTY_DAYS = "nextThirtyDays",
  NEXT_SIXTY_DAYS = "nextSixtyDays",
  LAST_THIRTY_DAYS = "lastThirtyDays",
  LAST_SIXTY_DAYS = "lastSixtyDays",
  DONE_TO_DATE = "doneToDate",
}

export const dateRangePickerChipToday = {
  value: DateRangePickerCommonChips.TODAY,
  label: "Aujourd'hui",
  startDate: () => today,
  endDate: () => today,
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipWeek = {
  value: DateRangePickerCommonChips.CURRENT_WEEK,
  label: "Cette semaine",
  startDate: () => startOfWeek(today, { locale: fr }),
  endDate: () => endOfWeek(today, { locale: fr }),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastWeek = {
  value: DateRangePickerCommonChips.LAST_WEEK,
  label: "La semaine dernière",
  startDate: () => startOfWeek(subWeeks(today, 1), { locale: fr }),
  endDate: () => endOfWeek(subWeeks(today, 1), { locale: fr }),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastTwoWeeks = {
  value: DateRangePickerCommonChips.LAST_TWO_WEEKS,
  label: "Les 15 derniers jours",
  startDate: () => subDays(today, 15),
  endDate: () => today,
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipMonth = {
  value: DateRangePickerCommonChips.CURRENT_MONTH,
  label: "Ce mois-ci",
  startDate: () => startOfMonth(today),
  endDate: () => endOfMonth(today),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastMonth = {
  value: DateRangePickerCommonChips.LAST_MONTH,
  label: "Le mois dernier",
  startDate: () => startOfMonth(subMonths(today, 1)),
  endDate: () => endOfMonth(subMonths(today, 1)),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastThreeMonths = {
  value: DateRangePickerCommonChips.LAST_THREE_MONTHS,
  label: "Les 3 derniers mois",
  startDate: () => startOfMonth(subMonths(today, 3)),
  endDate: () => startOfMonth(subMonths(today, 1)),
};

export const dateRangePickerChipExpiredToDate = {
  value: DateRangePickerCommonChips.EXPIRED_TO_DATE,
  label: "Expiré à date",
  startDate: () => new Date(0),
  endDate: () => today,
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipNextMonth = {
  value: DateRangePickerCommonChips.NEXT_MONTH,
  label: "Le mois prochain",
  startDate: () => startOfMonth(addMonths(today, 1)),
  endDate: () => endOfMonth(addMonths(today, 1)),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipNextWeek = {
  value: DateRangePickerCommonChips.NEXT_WEEK,
  label: "La semaine prochaine",
  startDate: () => startOfWeek(addWeeks(today, 1), { locale: fr }),
  endDate: () => endOfWeek(addWeeks(today, 1), { locale: fr }),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipNextTwoWeeks = {
  value: DateRangePickerCommonChips.NEXT_TWO_WEEKS,
  label: "Les 15 prochains jours",
  startDate: () => today,
  endDate: () => addDays(today, 15),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipNextThirtyDays = {
  value: DateRangePickerCommonChips.NEXT_THIRTY_DAYS,
  label: "Les 30 prochains jours",
  startDate: () => today,
  endDate: () => addDays(today, 30),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipNextSixtyDays = {
  value: DateRangePickerCommonChips.NEXT_SIXTY_DAYS,
  label: "Les 60 prochains jours",
  startDate: () => today,
  endDate: () => addDays(today, 60),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastThirtyDays = {
  value: DateRangePickerCommonChips.LAST_THIRTY_DAYS,
  label: "Les 30 derniers jours",
  startDate: () => subDays(today, 30),
  endDate: () => today,
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipYear = {
  value: DateRangePickerCommonChips.CURRENT_YEAR,
  label: "Cette année",
  startDate: () => startOfYear(today),
  endDate: () => endOfYear(today),
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipLastSixtyDays = {
  value: DateRangePickerCommonChips.LAST_SIXTY_DAYS,
  label: "Les 60 derniers jours",
  startDate: () => subDays(today, 60),
  endDate: () => today,
} as const satisfies IDateRangePickerChip;

export const dateRangePickerChipDoneToDate = {
  value: DateRangePickerCommonChips.DONE_TO_DATE,
  label: "Terminé à date",
  startDate: () => new Date(0),
  endDate: () => today,
} as const satisfies IDateRangePickerChip;
